import { create } from '@thoughtspot/logger';
import {
    Context,
    getTracer,
    otelContext,
    otelTrace,
    Span,
    SpanAttributes,
} from '@thoughtspot/observability';
import _ from 'lodash';
import { FLAGS, flags } from '../config-service/flags-service';

const logger = create('otel-trace-context-manager');
const debugLog = (...args: any[]) => {
    if (flags.getValue(FLAGS.debug?.name)) {
        // eslint-disable-next-line no-console
        console.log(...args);
    }
};

let rootSpan: Span | null = null;
let rootContext: Context | null = null;
let rootSpanEndTimer: any = null;
const ROOT_TRACER_NAME = 'blink-v2-root';

// Flag to track if telemetry is enabled
let isTelemetryEnabled = false;

// Callbacks registered by useTracing when telemetry isn't ready on first render
const readyCallbacks = new Set<() => void>();

export const onTelemetryReady = (cb: () => void): (() => void) => {
    if (isTelemetryEnabled) {
        cb();
        return () => _.noop();
    }
    readyCallbacks.add(cb);
    return () => readyCallbacks.delete(cb);
};

export const setTelemetryEnabled = (enabled: boolean) => {
    isTelemetryEnabled = enabled;
    if (enabled) {
        // Defer to next tick so initTelemetry + startRootSpan complete
        // synchronously before components re-render via the callbacks.
        setTimeout(() => {
            readyCallbacks.forEach(cb => cb());
            readyCallbacks.clear();
        }, 0);
    }
};

export const isTelemetryInitialized = () => isTelemetryEnabled;

// Root span lives long enough to be in the same export batch as long-running pages
// (e.g. liveboard loads that take 3-5s). Context stays alive for the full session.
const ROOT_SPAN_LIFETIME_MS = 5000;

export const startRootSpan = (
    spanName: string,
    attributes?: SpanAttributes,
) => {
    // Check if telemetry is enabled
    if (!isTelemetryEnabled) {
        logger.warn(
            '[TraceContext] Telemetry not enabled, skipping root span creation',
        );
        return null;
    }

    // Clear any pending end timer
    if (rootSpanEndTimer) {
        clearTimeout(rootSpanEndTimer);
    }

    // End previous root span if exists
    if (rootSpan && !rootSpan.isRecording()) {
        // Span already ended, just clear reference
        rootSpan = null;
    } else if (rootSpan) {
        rootSpan.end();
    }

    try {
        const tracer = getTracer(ROOT_TRACER_NAME);
        const parentContext = otelContext.active();

        rootSpan = tracer.startSpan(spanName, { attributes }, parentContext);
        rootContext = otelTrace.setSpan(parentContext, rootSpan);
    } catch (error) {
        // TracerProvider not ready yet or failed to initialize
        logger.warn('[TraceContext] Failed to create root span:', error);
        return null;
    }

    const spanCtx = rootSpan.spanContext();
    debugLog('[TraceContext] Created root span:', {
        name: spanName,
        traceId: spanCtx.traceId,
        spanId: spanCtx.spanId,
    });

    // End root span after ROOT_SPAN_LIFETIME_MS to ensure it exports in the same
    // batch as child spans. Context is kept alive so future spans still use it as parent.
    rootSpanEndTimer = setTimeout(() => {
        if (rootSpan && rootSpan.isRecording()) {
            debugLog(
                '[TraceContext] Auto-ending root span, spanId:',
                rootSpan.spanContext().spanId,
            );
            rootSpan.end();
        }
    }, ROOT_SPAN_LIFETIME_MS);

    return rootSpan;
};

export const getRootContext = (): Context | null => {
    return rootContext;
};

export const endRootSpan = () => {
    if (rootSpanEndTimer) {
        clearTimeout(rootSpanEndTimer);
    }
    if (rootSpan) {
        rootSpan.end();
        rootSpan = null;
        rootContext = null;
    }
};
